import type { ChangeStats, CleaningOptions } from '../types/app';

const listPattern = /^\s*(?:[-*+•‣▪◦]|\d+[.)])\s+\S/;
const headingPattern = /^(?:[A-ZÄÖÜ][^.!?]{0,65}|\d+(?:\.\d+)*\s+[^.!?]{1,60})$/;

function replacementCount(text: string, pattern: RegExp): number {
  return [...text.matchAll(pattern)].length;
}

function isProbableHeading(line: string, next: string): boolean {
  const value = line.trim();
  if (!headingPattern.test(value)) return false;
  if (value.length < 18) return true;
  return /^[A-ZÄÖÜ]/.test(next.trim()) && value.length < 55;
}

function isHyphenContinuation(current: string, next: string): boolean {
  const left = current.match(/([\p{L}]{3,})-\s*$/u)?.[1];
  const right = next.match(/^\s*([\p{Ll}]{2,})/u)?.[1];
  if (!left || !right) return false;
  return !/^(?:und|oder|aber|denn|der|die|das|ein|eine|im|am|an|zu)$/i.test(right);
}

function shouldJoinLine(current: string, next: string): boolean {
  const a = current.trim();
  const b = next.trim();
  if (!a || !b || listPattern.test(a) || listPattern.test(b)) return false;
  if (/^(?:https?:\/\/|www\.)/i.test(b) || /[.!?;:]$/.test(a)) return false;
  if (isProbableHeading(a, b)) return false;
  if (/^[\p{Ll}]/u.test(b)) return a.length >= 25;
  return a.length >= 55 && /[,)]$/.test(a);
}

export function repairPdfLineBreaks(text: string, stats: ChangeStats, joinHyphenation = true, joinLines = true): string {
  return text
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block.split('\n');
      const output: string[] = [];
      for (const line of lines) {
        if (output.length === 0) {
          output.push(line.trimEnd());
          continue;
        }
        const previous = output[output.length - 1] ?? '';
        if (joinHyphenation && isHyphenContinuation(previous, line)) {
          output[output.length - 1] = `${previous.replace(/-\s*$/, '')}${line.trimStart()}`;
          stats.hyphenationsJoined += 1;
        } else if (joinLines && shouldJoinLine(previous, line)) {
          output[output.length - 1] = `${previous.trimEnd()} ${line.trimStart()}`;
          stats.lineBreaksRepaired += 1;
        } else {
          output.push(line.trimEnd());
        }
      }
      return output.join('\n');
    })
    .join('\n\n');
}

export function cleanPlainText(input: string, options: CleaningOptions, stats: ChangeStats): string {
  let text = input.replace(/\r\n?/g, '\n');

  if (options.normalizeNbsp) {
    const count = replacementCount(text, /\u00a0/g);
    text = text.replace(/\u00a0/g, ' ');
    stats.nbspNormalized += count;
  }
  if (options.normalizeSpecialSpaces) {
    const count = replacementCount(text, /[\u2000-\u200A\u202F\u205F\u3000]/g);
    text = text.replace(/[\u2000-\u200A\u202F\u205F\u3000]/g, ' ');
    stats.specialSpacesNormalized += count;
  }
  if (options.removeZeroWidth) {
    const count = replacementCount(text, /[\u200B-\u200D\u2060\uFEFF]/g);
    text = text.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '');
    stats.invisibleCharsRemoved += count;
  }
  if (options.removeSoftHyphens) {
    const count = replacementCount(text, /\u00ad/g);
    text = text.replace(/\u00ad/g, '');
    stats.softHyphensRemoved += count;
  }
  if (options.duplicateSpaces) {
    text = text.replace(/[\t ]{2,}/g, (match) => {
      stats.duplicateSpacesRemoved += match.length - 1;
      return ' ';
    });
  }
  if (options.fixPunctuationSpacing) {
    text = text.replace(/\s+([,.;:!?])/g, (_match, punctuation: string) => {
      stats.punctuationSpacesFixed += 1;
      return punctuation;
    });
    text = text.replace(/([,;:!?])(?=[\p{L}\p{N}])/gu, (punctuation) => {
      stats.punctuationSpacesFixed += 1;
      return `${punctuation} `;
    });
    text = text.replace(/\.(?=[\p{Lu}])/gu, () => {
      stats.punctuationSpacesFixed += 1;
      return '. ';
    });
  }
  if (options.repairPdfBreaks) {
    text = repairPdfLineBreaks(text, stats, options.joinHyphenation);
  } else if (options.joinHyphenation) {
    text = repairPdfLineBreaks(text, stats, true, false);
  }
  if (options.excessBlankLines) {
    text = text.replace(/\n{4,}/g, (match) => {
      const removed = match.length - 3;
      stats.blankLinesReduced += removed;
      return '\n\n\n';
    });
  }
  if (!options.preserveParagraphs) {
    text = text.replace(/\n{2,}/g, (match) => {
      stats.blankLinesReduced += match.length - 1;
      return '\n';
    });
  }
  return text.trim();
}

export function isListLine(line: string): boolean {
  return listPattern.test(line);
}
